import axios from "axios";
import { isStunMSessionExpiry } from "./sessionRefresh";

// AuthContext persists values with JSON.stringify, while some older callers
// wrote the raw JWT. Accept both forms and never put quote characters into a
// Bearer token header.
export const normalizeToken = (value) => {
  let token = String(value || "").trim();
  if (token.length > 1 && token.startsWith('"') && token.endsWith('"')) {
    try { token = JSON.parse(token); } catch (_) { token = token.slice(1, -1); }
  }
  return String(token || "").trim();
};

export const getApiContext = () => {
  const context = typeof window !== "undefined" ? window.__authContext : null;
  return {
    apiUrl: context?.apiUrl || process.env.REACT_APP_API_URL || "",
    // Legacy persisted tokens must never override the restored cookie session.
    token: normalizeToken(context?.token || ""),
  };
};

export const authConfig = (token) => {
  const normalized = normalizeToken(token);
  return normalized
    ? { headers: { Authorization: `Bearer ${normalized}` }, withCredentials: true }
    : { withCredentials: true };
};

// Send the HttpOnly session cookie even after a page reload, when AuthContext
// has restored the session but deliberately has no JavaScript-readable JWT.
// A caller-supplied token still takes precedence in authConfig.
export const authArgs = (token) => [authConfig(token)];

export const safeUploadFilename = (value, fallback = "asset") => {
  const name = String(value || fallback)
    .split(/[\\/]/)
    .pop()
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 120);
  return name || fallback;
};

export const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .replace(/^(quotes|["'])|("|')$/g, "")
    .trim();

export const isAuthenticationError = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status !== 401) return false;
  // An unclassified 401 does not prove that the user's session expired.
  if (detail == null) return false;
  if (detail?.code === "AUTH_REFRESH_EXPIRED") return true;
  return isStunMSessionExpiry(error.response.data) && error?.sessionRefreshFailed === true;
};

// Kept as a small wrapper so future Quick Login/retry handling has one
// integration point without changing existing service function signatures.
export const requestWithAuth = async (request, token) => {
  try {
    return await request(token);
  } catch (error) {
    if (isAuthenticationError(error)) {
      error.authRetryable = true;
    }
    throw error;
  }
};

export { axios };
